import { useState, useEffect, useCallback } from 'react'
import {
  type Difficulty,
  type AttentionMode,
  type AttentionWord,
  type AttentionCondition,
  type AttentionRound,
  type CheckResult,
  buildRound,
  buildNumberRound,
  nextMark,
  checkAnswers,
  MARKS_FOR_DIFFICULTY,
} from './AttentionData'

interface ThemePool {
  slug:  string
  label: string
  words: string[]
}

export type { Difficulty, AttentionMode }

export interface AttentionState {
  words:       AttentionWord[]
  conditions:  AttentionCondition[]
  moves:       number
  startTime:   number | null
  endTime:     number | null
  checkResult: CheckResult | null
  gameComplete: boolean
  wrongFlashIds: number[]
}

function initState(
  difficulty:   Difficulty,
  language:     'es' | 'en',
  themes:       ThemePool[],
  classicWords: { es: string[]; en: string[] } | undefined,
  mode:         AttentionMode,
): AttentionState {
  const round: AttentionRound = mode === 'numbers'
    ? buildNumberRound(difficulty)
    : buildRound(difficulty, language, themes, classicWords)
  return {
    words:        round.words,
    conditions:   round.conditions,
    moves:        0,
    startTime:    null,
    endTime:      null,
    checkResult:  null,
    gameComplete: false,
    wrongFlashIds: [],
  }
}

export function useAttention(
  difficulty:   Difficulty,
  language:     'es' | 'en',
  themes:       ThemePool[],
  classicWords: { es: string[]; en: string[] } | undefined,
  mode:         AttentionMode,
) {
  const [state, setState] = useState<AttentionState>(() =>
    initState(difficulty, language, themes, classicWords, mode),
  )

  const resetGame = useCallback(() => {
    setState(initState(difficulty, language, themes, classicWords, mode))
  }, [difficulty, language, themes, classicWords, mode])

  // Re-init when themes/difficulty/language/mode change (async load)
  useEffect(() => {
    setState(initState(difficulty, language, themes, classicWords, mode))
  }, [difficulty, language, themes, classicWords, mode])

  // Clear wrong-flash after 650 ms
  useEffect(() => {
    if (state.wrongFlashIds.length === 0) return
    const t = setTimeout(() => {
      setState(prev => ({ ...prev, wrongFlashIds: [] }))
    }, 650)
    return () => clearTimeout(t)
  }, [state.wrongFlashIds])

  // Auto-complete when all words are correctly marked
  useEffect(() => {
    if (state.gameComplete) return
    const result = checkAnswers(state.words, state.conditions, themes)
    if (result.correct === result.total) {
      setState(prev => ({
        ...prev,
        gameComplete: true,
        endTime: Date.now(),
        checkResult: result,
      }))
    }
  }, [state.words, state.conditions, state.gameComplete, themes])

  const tapWord = useCallback((wordId: number) => {
    const availableMarks = MARKS_FOR_DIFFICULTY[difficulty]
    setState(prev => {
      if (prev.gameComplete) return prev
      const startTime = prev.startTime ?? Date.now()
      return {
        ...prev,
        startTime,
        moves: prev.moves + 1,
        checkResult: null,
        words: prev.words.map(w =>
          w.id === wordId ? { ...w, mark: nextMark(w.mark, availableMarks) } : w,
        ),
      }
    })
  }, [difficulty])

  const submitCheck = useCallback(() => {
    setState(prev => {
      if (prev.gameComplete) return prev
      const result = checkAnswers(prev.words, prev.conditions, themes)
      const gameComplete = result.correct === result.total
      return {
        ...prev,
        checkResult:   result,
        gameComplete,
        endTime:       gameComplete ? Date.now() : prev.endTime,
        wrongFlashIds: result.wrongIds,
      }
    })
  }, [themes])

  return { state, tapWord, submitCheck, resetGame }
}
